import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  QrCodeScannerOutlined,
  CheckCircleOutlined,
  CameraAltOutlined,
  StopCircleOutlined,
  WarningAmberOutlined,
  BadgeOutlined,
} from '@mui/icons-material';
import { ticketService } from '../services/ticketService';
import QrScanner from 'qr-scanner';

interface ParticipantInfo {
  name: string;
  ticket_type: string;
  payment_status?: string;
}

interface EventInfo {
  title: string;
  start_date: string;
}

interface TicketInfo {
  id: string;
  code: string | null;
  status: string;
  enabled: boolean;
  checked_in: boolean;
  participant: ParticipantInfo;
  event: EventInfo;
  synthetic?: boolean;
}

type InputMode = 'code' | 'cpf';

const TicketCheckIn: React.FC = () => {
  const [inputMode, setInputMode] = useState<InputMode>('code');
  const [ticketCode, setTicketCode] = useState('');
  const [cpf, setCpf] = useState('');
  const [ticketInfo, setTicketInfo] = useState<TicketInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [forceEnable, setForceEnable] = useState(false);
  const [showQrReader, setShowQrReader] = useState(true);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);
  const handleQrScanRef = useRef<(data: string) => void>(() => { });
  const lastScannedRef = useRef<string>('');

  useEffect(() => {
    if (showQrReader && inputMode === 'code' && videoRef.current) {
      startQrScanner();
    } else {
      stopQrScanner();
    }

    return () => {
      stopQrScanner();
    };
  }, [showQrReader, inputMode]);

  const handleQrScan = (data: string) => {
    if (!data) return;
    if (data === lastScannedRef.current) return;
    if (loading) return; 

    lastScannedRef.current = data;
    handleValidateTicket(data);

    setTimeout(() => { lastScannedRef.current = ''; }, 3000);
  };
  handleQrScanRef.current = handleQrScan;
  const startQrScanner = async () => {
    if (!videoRef.current) return;

    try {
      setCameraError(null);

      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          handleQrScanRef.current(result.data);
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
        }
      );

      await qrScannerRef.current.start();
    } catch (error) {
      console.error('Erro ao iniciar scanner:', error);
      setCameraError('Erro ao acessar a câmera. Verifique as permissões.');
    }
  };

  const stopQrScanner = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
  };

  const formatCpf = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: InputMode | null) => {
    if (!newMode) return;
    setInputMode(newMode);
    setTicketCode('');
    setCpf('');
    setTicketInfo(null);
    setError(null);
    setSuccess(null);
    setForceEnable(false);

    if (newMode === 'cpf') {
      setShowQrReader(false);
    }
  };

  const handleValidateTicket = async (code?: string) => {
    setError(null);
    setSuccess(null);

    // --- Modo CPF ---
    if (inputMode === 'cpf') {
      const rawCpf = cpf.replace(/\D/g, '');
      if (rawCpf.length !== 11) {
        setError('CPF inválido. Digite os 11 dígitos.');
        return;
      }
      try {
        setLoading(true);
        setCameraError(null);
        const response = await ticketService.validateByCpf(rawCpf);
        setTicketInfo(response.data.ticket);
      } catch (error: any) {
        setError(error?.response?.data?.message || 'Nenhum ticket encontrado para este CPF');
        setTicketInfo(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    // --- Modo código / QR ---
    const codeToValidate = code || ticketCode;
    if (!codeToValidate.trim()) {
      setError('Digite ou escaneie o código do ingresso');
      return;
    }
    try {
      setLoading(true);
      setCameraError(null);
      const response = await ticketService.validateTicket(codeToValidate);
      setTicketInfo(response.data.ticket);
      setTicketCode(codeToValidate);
    } catch (error: any) {
      setError(error?.response?.data?.message || 'Erro ao validar ingresso');
      setTicketInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!ticketInfo) return;

    try {
      setLoading(true);

      if (inputMode === 'cpf') {
        await ticketService.checkInByCpf(cpf.replace(/\D/g, ''), forceEnable);
      } else {
        await ticketService.checkInTicket(ticketCode, forceEnable);
      }

      setSuccess(forceEnable ? 'Check-in realizado com habilitação forçada!' : 'Check-in realizado com sucesso!');
      setTicketInfo({ ...ticketInfo, checked_in: true, enabled: true });
      setConfirmDialog(false);
      setTicketCode('');
      setCpf('');
      setForceEnable(false);

      setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 3000);
    } catch (error: any) {
      setError(error?.response?.data?.message || 'Erro ao fazer check-in');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTicketCode('');
    setCpf('');
    setTicketInfo(null);
    setError(null);
    setSuccess(null);
    setCameraError(null);
    setForceEnable(false);
    if (!showQrReader && inputMode === 'code') {
      setShowQrReader(true);
    }
  };



  const getStatusColor = (ticket: TicketInfo) => {
    if (ticket.checked_in) return 'success';
    if (ticket.status === 'open') return 'warning';
    if (ticket.enabled) return 'primary';
    return 'warning';
  };

  const getStatusText = (ticket: TicketInfo) => {
    if (ticket.checked_in) return 'Check-in Realizado';
    if (ticket.status === 'open') return 'Pagamento em aberto';
    if (ticket.enabled) return 'Válido para Check-in';
    return 'Não Habilitado';
  };

  const canCheckIn = ticketInfo && (ticketInfo.enabled || forceEnable) && !ticketInfo.checked_in;

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          <QrCodeScannerOutlined sx={{ mr: 2, verticalAlign: 'middle' }} />
          Check-in de Ingressos
        </Typography>

        <Card sx={{ mt: 4 }}>
          <CardContent>
            {/* Seletor de modo */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Identificar participante por:
              </Typography>
              <ToggleButtonGroup
                value={inputMode}
                exclusive
                onChange={handleModeChange}
                size="small"
              >
                <ToggleButton value="code" aria-label="código ou QR">
                  <QrCodeScannerOutlined sx={{ mr: 1, fontSize: 18 }} />
                  QR Code / Código
                </ToggleButton>
                <ToggleButton value="cpf" aria-label="CPF">
                  <BadgeOutlined sx={{ mr: 1, fontSize: 18 }} />
                  CPF
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                {inputMode === 'code' ? (
                  <TextField
                    fullWidth
                    label="Código do Ingresso"
                    value={ticketCode}
                    onChange={(e) => setTicketCode(e.target.value)}
                    placeholder="Ex: AVV-123456-ABCDEF"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleValidateTicket();
                    }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="CPF do Participante"
                    value={cpf}
                    onChange={(e) => setCpf(formatCpf(e.target.value))}
                    placeholder="000.000.000-00"
                    inputProps={{ maxLength: 14 }}
                    helperText="Digite o CPF cadastrado no ingresso"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleValidateTicket();
                    }}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleValidateTicket()}
                  disabled={loading}
                  sx={{ height: '56px' }}
                >
                  {loading ? 'Validando...' : 'Validar Ingresso'}
                </Button>
              </Grid>
            </Grid>

            {/* Controles de câmera — só visíveis no modo código */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button variant="outlined" size="small" onClick={resetForm}>
                Limpar
              </Button>

              <FormControlLabel
                control={
                  <Switch
                    checked={showQrReader && inputMode === 'code'}
                    onChange={(e) => setShowQrReader(e.target.checked)}
                    color="primary"
                    disabled={inputMode === 'cpf'}
                  />
                }
                label="Usar Câmera"
              />

              {showQrReader && inputMode === 'code' ? (
                <StopCircleOutlined color="error" />
              ) : (
                <CameraAltOutlined color={inputMode === 'cpf' ? 'disabled' : 'action'} />
              )}
            </Box>

            {/* QR CODE READER — só no modo código */}
            {showQrReader && inputMode === 'code' && (
              <Box sx={{ mt: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom align="center">
                      Escaneie o QR Code do Ingresso
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <video
                        ref={videoRef}
                        style={{
                          width: '100%',
                          maxWidth: '400px',
                          height: '300px',
                          border: '2px solid #1976d2',
                          borderRadius: '8px',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>

                    <Typography variant="caption" display="block" align="center" sx={{ mt: 2 }}>
                      Posicione o QR Code dentro da área da câmera
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setShowQrReader(false)}
                        startIcon={<StopCircleOutlined />}
                      >
                        Parar Câmera
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {cameraError && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {cameraError}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 3 }}>
            {success}
          </Alert>
        )}

        {/* INFO DO TICKET */}
        {ticketInfo && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Typography variant="h6">Informações do Ingresso</Typography>
                <Chip
                  label={getStatusText(ticketInfo)}
                  color={getStatusColor(ticketInfo)}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Evento
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {ticketInfo.event.title}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Data do Evento
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(ticketInfo.event.start_date).toLocaleDateString('pt-BR')}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Participante
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {ticketInfo.participant.name}
                  </Typography>
                  {ticketInfo.participant.payment_status && (
                    <Typography variant="body2" color="text.secondary">
                      Status de pagamento: {ticketInfo.participant.payment_status}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tipo de Ingresso
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {ticketInfo.participant.ticket_type}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Código
                  </Typography>
                  <Typography variant="h6" fontFamily="monospace">
                    {ticketInfo.code}
                  </Typography>
                </Grid>

                {/* Identificado por CPF — badge informativo */}
                {inputMode === 'cpf' && (
                  <Grid item xs={12}>
                    <Chip
                      icon={<BadgeOutlined />}
                      label="Identificado via CPF"
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                )}
              </Grid>

              {!ticketInfo.checked_in && (
                <Box sx={{ mt: 3 }}>
                  {!ticketInfo.enabled && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        ⚠️ Este ingresso não está habilitado para check-in.
                        Use a opção "Forçar habilitação" apenas em casos especiais.
                      </Typography>
                    </Alert>
                  )}

                  {!ticketInfo.enabled && (
                    <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={forceEnable}
                            onChange={(e) => setForceEnable(e.target.checked)}
                            color="warning"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <WarningAmberOutlined sx={{ mr: 1, color: 'warning.main' }} />
                            <Typography variant="body2" color="warning.main">
                              Forçar habilitação e check-in (usar apenas em casos especiais)
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  )}

                  {canCheckIn && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        color={forceEnable ? 'warning' : 'success'}
                        size="large"
                        startIcon={<CheckCircleOutlined />}
                        onClick={() => setConfirmDialog(true)}
                      >
                        {forceEnable ? 'Fazer Check-in Forçado' : 'Fazer Check-in'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {ticketInfo.checked_in && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Alert severity="info">
                    <Typography variant="body1">
                      Este ingresso já passou pelo check-in
                    </Typography>
                  </Alert>
                </Box>
              )}

              {!ticketInfo.enabled && !ticketInfo.checked_in && !forceEnable && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Alert severity="warning">
                    <Typography variant="body1">
                      Este ingresso ainda não está habilitado para check-in. Verifique se o pagamento foi confirmado.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Dialog de Confirmação */}
        <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
          <DialogTitle>
            {forceEnable ? 'Confirmar Check-in Forçado' : 'Confirmar Check-in'}
          </DialogTitle>
          <DialogContent>
            {forceEnable && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Atenção:</strong> Este check-in irá habilitar o ticket automaticamente,
                  mesmo sem confirmação de pagamento.
                </Typography>
              </Alert>
            )}
            <Typography>
              Deseja confirmar o check-in para o participante{' '}
              <strong>{ticketInfo?.participant.name}</strong>?
            </Typography>
            {inputMode === 'cpf' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Identificado via CPF: <strong>{cpf}</strong>
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Esta ação não pode ser desfeita.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(false)}>Cancelar</Button>
            <Button
              onClick={handleCheckIn}
              variant="contained"
              color={forceEnable ? 'warning' : 'success'}
              disabled={loading}
            >
              {loading
                ? 'Confirmando...'
                : forceEnable
                  ? 'Confirmar Check-in Forçado'
                  : 'Confirmar Check-in'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default TicketCheckIn;